import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../services/API/api.service';
import { AlertService } from '../../services/Alert/alert.service';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.scss']
})
export class OrderDetailsComponent implements OnInit {

  orderDetails: any = {};
  isLoading: boolean = false;
  orderId: number = 0;
  orderTips: number = 0;
  orderTax: number = 0;

  constructor(
    private apiService: ApiService,
    private alertService: AlertService,
    private route: ActivatedRoute
  ) {
    this.route.params.subscribe(params => {
      if(params.id){
        this.orderId = params.id;
        this.getOrderDetails();
      }
    });
  }

  ngOnInit(): void {}

  getOrderDetails(){
    this.isLoading = true;
    this.apiService.orderDetails(this.orderId).subscribe({
      next: response => {
        this.orderDetails = response.data.OrderDetails;
        console.log(this.orderDetails);
        this.isLoading = false;
      }, 
      error: (error: HttpErrorResponse) => {
        this.alertService.danger(error.error.message);
        this.isLoading = false;
      }
    });
  }

  // Round to nearest 10 cents (0.10)
  roundToNearestTen(value: number): number {
    return Math.round(value * 10) / 10;
  }

  // Calculate extras total for a product (multiplied by product quantity)
  calculateExtrasTotal(extras: any[], productQuantity: number): number {
    if (!extras || extras.length === 0) return 0;
    
    const extrasTotal = extras.reduce(
      (sum: number, extra: any) => sum + (extra.price * (extra.quantity || 1) * productQuantity), 
      0
    );
    
    return this.roundToNearestTen(extrasTotal);
  }

  // Calculate sides total for a product (multiplied by product quantity)
  calculateSidesTotal(sides: any[], productQuantity: number): number {
    if (!sides || sides.length === 0) return 0;
    
    const sidesTotal = sides.reduce(
      (sum: number, side: any) => sum + (side.price * (side.quantity || 1) * productQuantity), 
      0
    );
    
    return this.roundToNearestTen(sidesTotal);
  }

  // Calculate product base total (price * quantity)
  calculateProductBaseTotal(price: number, quantity: number): number {
    const total = price * quantity;
    return this.roundToNearestTen(total);
  }

  // Calculate complete product total including extras and sides
  calculateProductTotal(product: any): number {
    const baseTotal = this.calculateProductBaseTotal(product.price, product.quantity);
    const extrasTotal = this.calculateExtrasTotal(product.extras, product.quantity);
    const sidesTotal = this.calculateSidesTotal(product.sides, product.quantity);
    
    const productTotal = baseTotal + extrasTotal + sidesTotal;
    return this.roundToNearestTen(productTotal);
  }

  // Calculate subtotal with rounding including all products, extras, and sides
  calculateSubtotal(): number {
    if (!this.orderDetails?.products) return 0;
    
    const subtotal = this.orderDetails.products.reduce(
      (sum: number, product: any) => sum + this.calculateProductTotal(product), 
      0
    );
    
    return this.roundToNearestTen(subtotal);
  }

  // Get rounded delivery cost
  getRoundedDeliveryCost(): number {
    if (!this.orderDetails?.deliveryCost) return 0;
    return this.roundToNearestTen(this.orderDetails.deliveryCost);
  }

  // Get rounded service cost
  getRoundedServiceCost(): number {
    if (!this.orderDetails?.serviceCost) return 0;
    return this.roundToNearestTen(this.orderDetails.serviceCost);
  }

  // Get rounded tips
  getRoundedTips(): number {
    return this.roundToNearestTen(this.orderTips);
  }

  // Calculate total with all rounded values
  calculateTotal(): number {
    const subtotal = this.calculateSubtotal();
    const delivery = this.getRoundedDeliveryCost();
    const service = this.getRoundedServiceCost();
    const tips = this.getRoundedTips();
    
    const total = subtotal + delivery + service + tips;
    return this.roundToNearestTen(total);
  }

  // Check if product has extras
  hasExtras(product: any): boolean {
    return product.extras && product.extras.length > 0;
  }

  // Check if product has sides
  hasSides(product: any): boolean {
    return product.sides && product.sides.length > 0;
  }

  // This will be used later when you implement tips
  updateOrderTotalWithTips(tipsAmount: number): void {
    this.orderTips = this.roundToNearestTen(tipsAmount);
  }
}